import { IconType } from "react-icons";
import {
  MdAutoAwesome,
  MdEventRepeat,
  MdLocalFireDepartment,
  MdMovie,
  MdOndemandVideo,
  MdRepeat,
  MdShoppingCart,
  MdStars,
  MdStorefront,
  MdTimer,
  MdToday,
  MdCollections,
} from "react-icons/md";

export type OptimizeSection = "integration" | "challenge";

export interface EventFormDef {
  label: string;
  placeholder: string;
  helper: string;
}

export interface OptimizeCardDef {
  id: string;
  title: string;
  section: OptimizeSection;
  icon: IconType;
  accent: string;
  tagline: string;
  description: string[];
  // eligibility warning rendered in red under the description
  notice?: string;
  benefits: string[];
  requirements?: { title: string; items: string[] };
  eventForm?: EventFormDef;
  purchaseEventForm?: EventFormDef;
  hasCreativeUpload?: boolean;
  hasChallengeSlots?: boolean;
  activatable?: boolean;
  recommendedTag?: string;
}

// A partner title as the UI consumes it — one entry per (game, platform), built
// from /dashboard/optimize/apps (see appToGame in liveStore).
export interface OptimizeGame {
  id: string; // `${platform}:${appId}`
  name: string;
  platform: "Android" | "iOS";
  // store artwork; the avatar falls back to gradient + initials when it's absent
  icon: string;
  gradient: string;
}

// Fallbacks for the error copy only: the real numbers come from the board's
// `rules` and from the 409 refusal, so nothing here is authoritative. Both rules
// are scoped per game+platform (app_id), confirmed by product.
const DEFAULT_MAX_ACTIVE_CHALLENGES = 3;
const DEFAULT_COOLDOWN_HOURS = 14 * 24;

export const maxChallengesError = (
  maxActive = DEFAULT_MAX_ACTIVE_CHALLENGES
) =>
  `You can participate in a maximum of ${maxActive} challenges per game at the same time. Please, pause one of the active challenges for this game to activate a new one.`;

// "once every 2 weeks" in the doc's copy; derived from the backend's
// activationCooldownHours so a changed rule doesn't leave the UI lying
export const cooldownPeriodLabel = (
  hours = DEFAULT_COOLDOWN_HOURS
): string => {
  if (hours % (24 * 7) === 0) {
    const weeks = hours / (24 * 7);
    return weeks === 1 ? "1 week" : `${weeks} weeks`;
  }
  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? "1 day" : `${days} days`;
  }
  return hours === 1 ? "1 hour" : `${hours} hours`;
};

export const cooldownError = (
  lastActivation: string,
  nextActivation: string,
  cooldownHours = DEFAULT_COOLDOWN_HOURS
) =>
  `New challenges can only be tested once every ${cooldownPeriodLabel(
    cooldownHours
  )}. Your last challenge was activated on ${lastActivation}. You can activate your next one on ${nextActivation}.`;

export const OPTIMIZE_CARDS: OptimizeCardDef[] = [
  {
    id: "recurring_event_structure",
    title: "Recurring Event Structure",
    section: "integration",
    icon: MdRepeat,
    accent: "purple",
    tagline:
      "Share recurring progression events to unlock dynamic, personalized task structures.",
    description: [
      'Share recurring events such as "level completed" - an event that fires every time a user completes a level. This allows the system to build dynamic structures that adapt to each user\'s progress rather than a fixed set of tasks.',
    ],
    benefits: [
      "Personalized reward structures generated per user based on actual progress",
      "Progress bars that motivate users to complete their tasks",
      "Eligibility for bonus challenges that require recurring events",
      "Continuous structures that run for months with no further manual updates",
    ],
    requirements: {
      title: "Requirements",
      items: [
        "One event per level - events covering multiple levels (e.g. every 5th or 10th level) are not supported",
        "Must reflect the game's core progression (levels, kingdoms, chapters, etc.)",
        "The value must only increase - resources such as gold or coins are not eligible as they can decrease",
      ],
    },
    eventForm: {
      label: "Progression event name",
      placeholder: "e.g. level_completed",
      helper:
        "Events are platform specific. Add the event name your game fires on every level completion.",
    },
    recommendedTag: "Recommended",
  },
  {
    id: "rewarded_ad_events",
    title: "Rewarded Ad Events",
    section: "integration",
    icon: MdOndemandVideo,
    accent: "cyan",
    tagline:
      "Share rewarded ad events and your users automatically receive rewarded ad tasks.",
    description: [
      "Once you share rewarded ad events with us, your users will automatically start receiving rewarded ad tasks in addition to your regular structure.",
      "Participation requires a rewarded video ad event. The event must be recurring and must fire on completion of a rewarded video ad (not at the start).",
    ],
    benefits: [
      "Directly increases rewarded ad impressions and IAA revenue",
      "Improves user quality, retention and engagement within your app",
      "Works automatically once the event is shared - no ongoing setup required",
      "Makes your game eligible for bonus challenges that require rewarded ad events",
    ],
    requirements: {
      title: "Event requirements",
      items: [
        "Type: rewarded video ad (banners not accepted)",
        "Trigger: on ad completion, not on ad start",
        "Frequency: recurring - fires on every completion",
      ],
    },
    eventForm: {
      label: "Rewarded ad event name",
      placeholder: "e.g. rewarded_ad_completed",
      helper:
        "Events are platform specific. The event must fire on every rewarded video completion.",
    },
    recommendedTag: "Recommended for Ad Monetized Games",
  },
  {
    id: "override_creatives",
    title: "Override Creatives",
    section: "integration",
    icon: MdMovie,
    accent: "pink",
    tagline:
      "Upload your best converting creatives instead of relying on store assets.",
    description: [
      "We import creatives automatically from the store. Any change you make on the store is pulled by the system in real time. On top of that, creatives are served based on the language of the user.",
      "However, you can also manually override those to upload your best converting creatives.",
    ],
    benefits: [
      "Upload your best converting creatives instead of relying on store assets built for the storefront",
      "Test and compare different creatives to identify top performers",
      "Localized creatives per language increase relevance and click-through rate",
    ],
    requirements: {
      title: "Specs",
      items: [
        "Orientation: horizontal (landscape)",
        "Aspect ratio: 16:9 - recommended 1920×1080, minimum 1280×720",
        "Video Format: MP4 or MOV",
        "Image Format: JPG or PNG",
        "Max file size: 100 MB (video), 10 MB (image)",
        "Video Length: 15–30 seconds recommended",
      ],
    },
    hasCreativeUpload: true,
  },
  {
    id: "retention_challenges",
    title: "D3, D7, D30 Retention Challenges",
    section: "challenge",
    icon: MdStars,
    accent: "yellow",
    tagline:
      "Reward users for returning to your game with milestones at 3, 7 and 30 days.",
    description: [
      "Users are rewarded for returning to your game on consecutive days, with separate milestones at 3, 7 and 30 days. Each milestone carries a higher reward, so the incentive grows the longer they stay.",
    ],
    benefits: [
      "Directly improves D3, D7 and D30 retention",
      "Escalating rewards keep users engaged well beyond the first session",
      "Visible progress bars encourage users to complete the streak",
      "Runs continuously without additional setup",
    ],
    activatable: true,
  },
  {
    id: "live_ops_collectables_playtime",
    title: "Live Ops Collectables - Playtime",
    section: "challenge",
    icon: MdCollections,
    accent: "teal",
    tagline:
      "Users collect items across several games by reaching playtime targets.",
    description: [
      "Users collect items across several games by reaching a playtime target in each one, for example 15 minutes per title. Once the full set is collected, they receive the reward.",
    ],
    benefits: [
      "Increases session length and total playtime per user",
      "Cross-game format brings in users from other titles in the campaign",
      "Collection mechanic creates a clear goal and drives completion",
      "Seasonal themes are being rotated to keep the format fresh",
    ],
    activatable: true,
  },
  {
    id: "time_limited_challenges",
    title: "Time-Limited Challenges",
    section: "challenge",
    icon: MdTimer,
    accent: "orange",
    tagline:
      "Progression targets with a deadline - rewards only if the target is met in time.",
    description: [
      "Users are given a progression target with a deadline - for example, reaching Level 20 within 3 days or Level 100 within 7 days. The reward is only granted if the target is met in time.",
      "You can add up to 3 time-limited challenges per title.",
    ],
    benefits: [
      "Creates urgency and drives higher daily activity",
      "Pushes users deeper into the funnel in a short period",
      "Multiple tiers allow both casual and committed users to participate",
      "Strong indicator of user quality, as only engaged users complete them",
    ],
    hasChallengeSlots: true,
    activatable: true,
  },
  {
    id: "daily_playtime_challenges",
    title: "Daily Playtime Challenges",
    section: "challenge",
    icon: MdToday,
    accent: "blue",
    tagline:
      "Daily playtime targets across featured games - whitelist your title.",
    description: [
      "Users are given a daily target to play each of the featured games for a set amount of time, for example 5 minutes per title. The reward is granted once all games in the set have been played.",
      "Activate this challenge to whitelist your game and have it featured among the participating titles.",
    ],
    benefits: [
      "Increases daily sessions and total playtime per user",
      "Builds a daily habit, which supports long-term retention",
      "Cross-game format brings in users already active in other titles",
      "Refreshes daily, so users have a reason to return every day",
    ],
    activatable: true,
  },
  {
    id: "daily_rewarded_ad_challenges",
    title: "Daily Rewarded Ad Challenges",
    section: "challenge",
    icon: MdOndemandVideo,
    accent: "cyan",
    tagline:
      "Users watch a set number of rewarded ads across featured games every day.",
    description: [
      "Users are asked to watch a set number of rewarded ads across the featured games, for example 5 ads per day. Progress is shared across all participating titles, so any combination counts toward the goal.",
      "Activate this challenge to whitelist your game and have it featured among the participating titles.",
    ],
    notice:
      "To be eligible for this challenge, you need to share with us recurring Rewarded Ad Watched events.",
    benefits: [
      "Attracts users who are already used to engaging with rewarded ads",
      "Shared progress means users often watch several ads in a single session",
      "Refreshes daily, so users have a reason to return every day",
    ],
    activatable: true,
  },
  {
    id: "purchase_challenges",
    title: "Purchase Challenges",
    section: "challenge",
    icon: MdShoppingCart,
    accent: "green",
    tagline:
      "Users make 3 purchases within 3 days inside your game to earn the reward.",
    description: [
      "Users are given a target of making 3 purchases within 3 days inside your game. The reward is granted only if the target is met within the time limit.",
      "This challenge is available for games where IAP monetization share is higher than 50%.",
    ],
    benefits: [
      "Drives IAP revenue and increases purchase frequency",
      "Multiple purchases per user directly increases your ARPU and ARPPU",
      "Encourages first-time buyers to convert, and existing buyers to buy again",
    ],
    purchaseEventForm: {
      label: "Purchase event name",
      placeholder: "e.g. iap_purchase_completed",
      helper:
        "The event your game fires on every completed purchase. Pre-filled if we already have one on file.",
    },
    activatable: true,
  },
  {
    id: "burning_rewards",
    title: "Burning Rewards",
    section: "challenge",
    icon: MdLocalFireDepartment,
    accent: "red",
    tagline:
      "A short time window in which task rewards are multiplied, with a countdown.",
    description: [
      "Users are given a short time window in which rewards for their tasks are multiplied. The higher payout is visible from the start, with a countdown showing how long it stays active.",
    ],
    benefits: [
      "Increases playtime session length, as users keep playing to finish before the timer ends",
      "Drives a sharp spike in task completions during the boost window",
      "Reactivates users who had a task pending but never finished it",
    ],
    activatable: true,
  },
  {
    id: "multi_game_purchase_challenges",
    title: "Multi-Game Purchase Challenges",
    section: "challenge",
    icon: MdStorefront,
    accent: "green",
    tagline:
      "3 purchases across any eligible game - shared progress brings in proven spenders.",
    description: [
      "Users are given a target of 3 purchases that can be made across any of the eligible games in the challenge. Progress is shared, so purchases in any participating title count toward the goal.",
    ],
    benefits: [
      "Drives IAP revenue and increases purchase frequency, ARPU, ARPPU",
      "Brings in proven spenders who are already purchasing in other games",
      "Encourages first-time buyers to convert, and existing buyers to buy again",
    ],
    purchaseEventForm: {
      label: "Purchase event name",
      placeholder: "e.g. iap_purchase_completed",
      helper:
        "The event your game fires on every completed purchase. Pre-filled if we already have one on file.",
    },
    activatable: true,
  },
  {
    id: "live_ops_collectables_events",
    title: "Live Ops Collectables - Events",
    section: "challenge",
    icon: MdEventRepeat,
    accent: "purple",
    tagline:
      "Users collect items by completing progression tasks across several games.",
    description: [
      "Users collect items by completing progression tasks across several games, for example Finishing Level 10 or Building 3 Kingdoms. Each completed task awards one collectable item, and the reward is granted once the full set is collected.",
    ],
    benefits: [
      "Pushes users deeper into your game's core progression",
      "Cross-game format brings in users already active in other titles",
      "Collection mechanic creates a clear goal and drives completion",
      "Seasonal themes are being rotated to keep the format fresh",
    ],
    activatable: true,
  },
];

export const OPTIMIZE_ICON = MdAutoAwesome;

export const getCard = (id: string) =>
  OPTIMIZE_CARDS.find((card) => card.id === id);
